import {useLayoutEffect} from 'react';
import {Outlet, useLocation} from 'react-router-dom';

import {closeWebView} from '@zzng/common/scheme/web';

import Banners from '@/root/components/Banners';
import CardBenefits from '@/root/components/CardBenefits';
import MyCards from '@/root/components/MyCards';
import Notice from '@/root/components/Notice';
import RecommendCards from '@/root/components/RecommendCards';
import {LOCAL_STORAGE_KEY} from '@/root/constants/keys';
import {ROUTES} from '@/root/constants/routes';
import useTemporaryHiddenPopup from '@/root/hooks/useTemporaryHiddenPopup';
import {useVisibilityAOS} from '@/root/hooks/useVisibilityAOS';
import HomeSkeleton from '@/root/pages/home/skeleton';
import {useHome} from '@/root/queries/home';

import PageLayout, {PageDescription} from '@/shared/components/PageLayout';
import {PAGE_NAME, STATUS_BAR_COLOR} from '@/shared/constants/statusBarColor';
import {TIARA_ACTION, TIARA_PAGE_NAME} from '@/shared/constants/tiara';
import withSuspense from '@/shared/hoc/withSuspense';
import useAdvancedNavigate from '@/shared/hooks/useAdvancedNavigate';
import useHistoryBackAOS from '@/shared/hooks/useHistoryBackAOS';
import useKakaoSDK from '@/shared/hooks/useKakaoSDK';
import {useScrollRestoration} from '@/shared/hooks/useScrollRestoration';

const HomePage = () => {
  const {container} = useScrollRestoration();
  const navigate = useAdvancedNavigate();
  const {notice, banners, bestSection, benefits, cards, recommendCards, popupBanner} = useHome();
  const {isBindedHistoryBack} = useHistoryBackAOS({fn: closeWebView}, []);
  const {pathname} = useLocation();

  useTemporaryHiddenPopup({
    popupType: 'HOME_EVENT',
    popupProps: popupBanner,
    storageKey: LOCAL_STORAGE_KEY.HOME_EVENT_POPUP_DATE,
    hidden: pathname !== '/home'
  });

  useKakaoSDK();
  useVisibilityAOS(container);

  useLayoutEffect(() => {
    // ios에서 스크롤할 때 바운스 백그라운드 색과 페이지 배경색을 일치시키기 위해 필요
    // https://kakao.agit.in/g/436078/wall/407581129#comment_panel_413642089
    document.body.classList.add('bg_type1');

    return () => {
      document.body.classList.remove('bg_type1');
    };
  }, []);

  const onClickSetting = () => {
    navigate(ROUTES.SETTING, {keepSearchParams: true});
  };

  return (
    <>
      <PageLayout
        ref={container}
        tiara={{
          pageName: TIARA_PAGE_NAME.HOME,
          pageAction: TIARA_ACTION.HOME_VIEW,
          customProps: {
            group_code: cards.map((card) => card.key).join(', '),
            number_of_cards: cards.flatMap((card) => card.groupedCards).length.toString()
          }
        }}
        className="container-home"
        pageDescription={PageDescription.HOME}
        footerProps={{type: 'default', invisibleLabels: ['제휴제안']}}
        statusBarColor={{default: STATUS_BAR_COLOR[PAGE_NAME.HOME]}}
        headerProps={{
          title: '톡디지털카드',
          buttons: [{type: 'CLOSE'}, {type: 'SETTING', onClick: onClickSetting}]
        }}
      >
        <div className="inner_g">
          <Notice notice={notice} />
          <Banners banners={banners} />
          <MyCards cards={cards} />
          <RecommendCards cards={recommendCards} />
          <CardBenefits bestSection={bestSection} benefits={benefits} />
        </div>
      </PageLayout>
      {/** historyBack 이슈로 부모 컴포넌트가 먼저 렌더링 된 이후에 Outlet을 렌더링함 https://jira.daumkakao.com/browse/CARD-658 */}
      {isBindedHistoryBack && <Outlet />}
    </>
  );
};

export default withSuspense(HomePage, HomeSkeleton);
